import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api/client.js';
import { useToast } from '../context/ToastContext.jsx';
import PageHeader from '../components/PageHeader.jsx';
import LoadingState from '../components/LoadingState.jsx';
import EmptyState from '../components/EmptyState.jsx';
import StatusBadge from '../components/StatusBadge.jsx';
import Modal from '../components/Modal.jsx';

export default function PublishersPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [publishers, setPublishers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const list = await api.listPublishers();
      setPublishers(list);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load publishers.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!publishers) return [];
    return publishers.filter((p) => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (!search.trim()) return true;
      const q = search.trim().toLowerCase();
      return p.name?.toLowerCase().includes(q) || p.domain?.toLowerCase().includes(q);
    });
  }, [publishers, search, statusFilter]);

  return (
    <div className="page">
      <PageHeader
        title="Publishers"
        subtitle="Supply-side accounts connected to the exchange."
        actions={
          <button className="btn btn--primary" onClick={() => setShowCreate(true)}>
            + New publisher
          </button>
        }
      />

      <div className="toolbar">
        <input
          className="toolbar__search"
          placeholder="Search by name or domain&hellip;"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="toolbar__filters">
          {['all', 'active', 'paused', 'archived'].map((s) => (
            <button
              key={s}
              className={`chip${statusFilter === s ? ' chip--active' : ''}`}
              onClick={() => setStatusFilter(s)}
            >
              {s === 'all' ? 'All' : s[0].toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {loading && <LoadingState label="Loading publishers" />}
      {!loading && error && <EmptyState title="Couldn't load publishers" message={error} />}

      {!loading && !error && publishers && publishers.length === 0 && (
        <EmptyState
          title="No publishers yet"
          message="Create your first publisher to start connecting supply."
          action={
            <button className="btn btn--primary" onClick={() => setShowCreate(true)}>
              + New publisher
            </button>
          }
        />
      )}

      {!loading && !error && publishers && publishers.length > 0 && (
        <section className="card card--flush">
          {filtered.length === 0 ? (
            <EmptyState title="No matching publishers" message="Try a different search or filter." />
          ) : (
            <div className="table-scroll">
              <table className="data-table data-table--clickable">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Domain</th>
                    <th>GAM network</th>
                    <th>Status</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr key={p.id} onClick={() => navigate(`/publishers/${p.id}`)}>
                      <td className="cell-title">{p.name}</td>
                      <td>{p.domain}</td>
                      <td>{p.gam_network_id || '—'}</td>
                      <td>
                        <StatusBadge status={p.status} />
                      </td>
                      <td className="cell-muted">{formatDate(p.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {showCreate && (
        <CreatePublisherModal
          onClose={() => setShowCreate(false)}
          onCreated={(pub) => {
            setShowCreate(false);
            toast.success(`Publisher "${pub.name}" created.`);
            load();
            navigate(`/publishers/${pub.id}`);
          }}
        />
      )}
    </div>
  );
}

function CreatePublisherModal({ onClose, onCreated }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [gamNetworkId, setGamNetworkId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const body = { name: name.trim(), domain: domain.trim() };
      if (gamNetworkId.trim()) body.gam_network_id = gamNetworkId.trim();
      const created = await api.createPublisher(body);
      onCreated(created);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setFormError('That domain is already registered to another publisher.');
      } else {
        const msg = err instanceof ApiError ? err.message : 'Could not create publisher.';
        setFormError(msg);
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title="New publisher" onClose={onClose}>
      <form onSubmit={handleSubmit} className="form">
        <label className="field">
          <span className="field__label">Name</span>
          <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme News" />
        </label>
        <label className="field">
          <span className="field__label">Domain</span>
          <input required value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="acmenews.com" />
        </label>
        <label className="field">
          <span className="field__label">GAM network ID <span className="field__optional">optional</span></span>
          <input value={gamNetworkId} onChange={(e) => setGamNetworkId(e.target.value)} placeholder="123456789" />
        </label>
        {formError && <div className="form-error">{formError}</div>}
        <div className="form__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={submitting}>
            {submitting ? 'Creating&hellip;' : 'Create publisher'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
